import React, { useCallback, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import Mail from '@mui/icons-material/Mail'
import { makeStyles } from '@mui/styles'
import classnames from 'classnames'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { endOfDay, startOfDay } from 'date-fns'
import { submitDashboardReportRequest, submitEmailReportRequest } from '@api'
import SnackBar from '@component/SnackBar'
import SendEmail from '@/components/Modal/SendEmail'
import CustomDateRange from '@component/DateSelect/CustomDateRange'
import BackToHomeButton, { CurrentTabLabel } from '@/layout/BackToHomeButton'
import { useIsOneClick } from '@/dashboard/dashboardTheme'
import useDateRangeSearchParams from '@hooks/useDateRangeSearchParams'
import { usePermissions } from '@hooks/useHasPermission'
import { useEnabledFeatures } from '@hooks/useFeature'
import { getBuildingReports, getCategoryTabs, getPortfolioReports } from '@/analytics/reportRegistry'

interface Props {
  /**
   * Set on dashboards that render outside the analytics shell (the escapee
   * routes: work order, accounting, collections, deficiency, master notepad).
   * The shell's paper already supplies the page padding; standalone pages do
   * not, so the header supplies its own — same inset SectionTitle used.
   */
  standalone?: boolean
  /**
   * Drop the category title above the tab strip. Set by dashboards that carry
   * the redesigned white shell, which reads as its own heading; the title row
   * is then only drawn when it still has actions (email / date range) to hold.
   */
  hideTitle?: boolean
  /**
   * Portfolio Analytics (the `Global*` reports): tabs list the portfolio twins
   * and the email goes out as a group report. Building otherwise.
   */
  portfolio?: boolean
}

/**
 * Title row + category tab strip for an analytics report, building or
 * portfolio: the header half of the dashboard shell, lifted out so the escapee
 * dashboards render the identical pattern.
 *
 * The active category comes from whichever report the URL is on; the strip
 * lists that category's permitted reports using their real paths, so tabs cross
 * the shell boundary in both directions.
 */
const AnalyticsCategoryHeader: React.FC<Props> = ({
  standalone = false,
  hideTitle: hideTitleProp = false,
  portfolio = false,
}) => {
  const classes = useStyles()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const permissions = usePermissions()
  const features = useEnabledFeatures()
  const [searchParams] = useSearchParams()
  const [open, setOpen] = useState(false)
  const [dateRange, setDateRange] = useDateRangeSearchParams()
  const oneClick = useIsOneClick()
  const {
    selectedGroupId,
    selectedPropertyId,
    selectedPropertyName,
    propertyTag: { PropertyTagId = '' } = {},
  } = useSelector((state) => state.profile)
  const {
    bId = selectedPropertyId,
    gId = selectedGroupId,
    ptId = PropertyTagId,
  } = useParams<{ bId: string; gId: string; ptId: string }>()

  // Empty bId falls back to legacy paths (shim-handled) rather than /b//.
  const scope = bId ? `/b/${bId}` : ''
  const reports = useMemo(
    () => (portfolio ? getPortfolioReports(gId ? `/p/${gId}` : '', permissions, features) : getBuildingReports(scope, permissions, features)),
    [portfolio, scope, permissions, features]
  )
  const selectedItem = useMemo(() => reports.find((option) => pathname.includes(option.path)), [reports, pathname])
  const { category, tabs } = useMemo(() => getCategoryTabs(selectedItem, reports), [selectedItem, reports])

  const emailSendUrl = selectedItem?.emailSendUrl || false
  const emailDisabled = selectedItem?.emailDisabled || !selectedItem?.value
  // Redesigned dashboards draw the EMAIL pill at the end of their own filter row
  // (registry `emailInline`), so the header must not draw a second one.
  const showEmail = selectedItem?.showEmail !== false && !selectedItem?.emailInline

  const onTabChange = useCallback(
    (_: React.SyntheticEvent, tabValue: string) => {
      const tabPath = tabs.find((item) => item.value === tabValue)?.path
      if (tabPath) navigate(tabPath)
    },
    [tabs, navigate]
  )

  const onOpen = useCallback(() => setOpen(true), [])
  const onClose = useCallback(() => setOpen(false), [])

  const onSend = useCallback(
    (ContactEmails: string[], Emails: string[]) => {
      const {
        StartDate: storedStart = '',
        EndDate: storedEnd = '',
        tu,
        ...other
      } = JSON.parse(localStorage.getItem('DashboardDate') || '{}')
      // Prefer the range in the URL (?from/&to): localStorage is written by only
      // some reports, so it can hold a stale range left by a previously viewed
      // report. Reports migrate to useDateRangeSearchParams individually.
      const StartDate = searchParams.get('from') || storedStart
      const EndDate = searchParams.get('to') || storedEnd
      const api = emailSendUrl ? submitEmailReportRequest : submitDashboardReportRequest
      const path = (selectedItem?.path || '').substring(1)
      const pdfTitle = `${selectedItem?.label}: ${selectedPropertyName}`
      let transPdfUrl = selectedItem?.puppeteerUrl
        ? `${selectedItem?.puppeteerUrl}/${gId}/${ptId || 'ptId'}/${bId}/${StartDate}/${EndDate}/${pdfTitle}`
        : undefined
      if (selectedItem?.value === 'MasterNotepad') {
        transPdfUrl = `${selectedItem?.puppeteerUrl}/${gId}/${bId}/${other.ContentType || 'ContentProof'}`
      }
      if (selectedItem?.value === 'GlobalPortfolioMobileEmployee') {
        transPdfUrl = `${selectedItem?.puppeteerUrl}/${gId}/${other.bId || 'bId'}/${StartDate}/${EndDate}`
      }
      if (selectedItem?.value === 'TeamPerformance') {
        transPdfUrl = `${selectedItem?.puppeteerUrl}/${gId}/${bId}/${StartDate}/${EndDate}`
      }
      if (selectedItem?.value === 'CustomerFeedbackOnSR') {
        transPdfUrl = `${selectedItem?.puppeteerUrl}/${gId}/${bId}`
      }
      api({
        GroupId: gId,
        BuildingId: bId,
        DashboardName: selectedItem?.label,
        ReportType: selectedItem?.value,
        StartDate: StartDate && startOfDay(new Date(StartDate)).getTime(),
        EndDate: EndDate && endOfDay(new Date(EndDate)).getTime(),
        Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...other,
        ContactEmails,
        Emails,
        // The scoped path already carries /b/:bId, and the backend appends this
        // verbatim to the portal host for the email's "view dashboard" link.
        Path: path,
        TransPdfUrl: transPdfUrl,
      }).then(() => {
        SnackBar({ msg: 'The dashboard has been sent!', type: 'success' })
        setOpen(false)
      })
    },
    [searchParams, emailSendUrl, selectedItem, selectedPropertyName, gId, ptId, bId]
  )

  const headerDateRange = !!selectedItem?.urlDateRange && !selectedItem?.dateRangeInline
  const hasActions = headerDateRange || showEmail
  // From the route (standalone escapees) or from the report's registry entry (reports
  // rendered inside the dashboard shell, which mounts this header itself).
  // The sidebar already names the category the user is in, so the title row never repeats
  // it; the prop / registry flag are kept for reference but every report hides now.
  const hideTitle = true
  void hideTitleProp
  // A strip with one tab names nothing the sidebar doesn't already: skip it. With the
  // title hidden too, the header draws nothing and the dashboard takes the whole page.
  // One Click never lists the category's other reports: its tile opened this one, so
  // the row holds Back to Home and this report's name instead.
  const showTabs = tabs.length > 1 && !oneClick
  const showCurrent = oneClick && !!selectedItem

  if (hideTitle && !hasActions && !showTabs && !showCurrent) return null

  return (
    <div className={classnames({ [classes.standalone]: standalone })}>
      {(!hideTitle || hasActions) && (
        <Box className={classes.titleRow}>
          {!hideTitle && <Typography className={classes.title}>{category?.label || 'Analytics'}</Typography>}
          <Box className={classes.titleActions}>
            {headerDateRange && (
              <CustomDateRange value={dateRange} onChange={setDateRange} label="Date Range" />
            )}
            {showEmail && (
              <Button variant="outlined" startIcon={MAIL_ICON} disabled={emailDisabled} onClick={onOpen}>
                EMAIL
              </Button>
            )}
          </Box>
        </Box>
      )}
      {showTabs && (
        <Box className={classes.tabRow}>
          <Tabs
            value={selectedItem?.value ?? false}
            className={classes.tabs}
            onChange={onTabChange}
            scrollButtons="auto"
            variant="scrollable"
          >
            {tabs.map((item) => (
              <Tab
                key={item.value}
                label={item.label}
                value={item.value}
                className={classnames(classes.tab, {
                  // Rounds & Inspections carries six tabs — double-stack the labels
                  // (wrap to two lines) to save horizontal space (nav reorg spec §3).
                  [classes.tabStacked]: category?.slug === 'rounds',
                })}
              />
            ))}
          </Tabs>
          <BackToHomeButton />
        </Box>
      )}
      {showCurrent && (
        <Box className={classes.tabRow}>
          <BackToHomeButton leading />
          <CurrentTabLabel label={selectedItem?.label || ''} />
        </Box>
      )}
      {open && (
        <SendEmail onClose={onClose} onSend={onSend} onlyContact={emailSendUrl} gId={gId} isGroup={portfolio} />
      )}
    </div>
  )
}

const MAIL_ICON = <Mail />

const useStyles = makeStyles(() => ({
  standalone: {
    backgroundColor: '#F5F5F5',
    padding: '24px 24px 0',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #BCBCBC',
    marginBottom: 16,
    paddingBottom: 8,
  },
  titleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  title: {
    color: '#000',
    fontSize: '32px',
    fontWeight: 500,
  },
  // The strip plus One Click's "Back to Home" right after the last tab.
  tabRow: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #ccc',
    marginBottom: 24,
  },
  tabs: {
    minHeight: 50,
    minWidth: 0,
    '& .MuiTabs-indicator': { backgroundColor: '#4A3D9E' },
  },
  tab: {
    fontSize: 20,
    textTransform: 'none',
    color: '#707070',
    padding: '12px 16px',
    fontWeight: 400,
    // Report labels are long ("Equipment Detail Collection") — wrapping them
    // inside the fixed-height strip clips to two squished lines. Keep each
    // label on one line and let the scrollable variant handle overflow.
    whiteSpace: 'nowrap',
    // Indigo, matching the app bar gradient's start (layout/AppBarExtend.tsx).
    '&.Mui-selected': {
      color: '#4A3D9E',
      fontWeight: 500,
    },
  },
  // The double-stacked variant: wraps at a narrow width with room for two
  // lines, so a six-tab strip fits without scrolling.
  tabStacked: {
    whiteSpace: 'normal',
    maxWidth: 150,
    lineHeight: 1.2,
    padding: '8px 12px',
  },
}))

export default React.memo(AnalyticsCategoryHeader)
